import React, { useState, useCallback } from 'react';
import { Upload, X, FileSpreadsheet, Download, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { asignacionesAPI } from '../services/api';

function ImportadorAsignaciones({ onClose, onImportComplete }) {
  const [archivo, setArchivo] = useState(null);
  const [arrastrando, setArrastrando] = useState(false);
  const [modo, setModo] = useState('estricto');
  const [sobrescribir, setSobrescribir] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState(null);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setArrastrando(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setArrastrando(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setArrastrando(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      validarYSetearArchivo(file);
    }
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      validarYSetearArchivo(file);
    }
  };

  const validarYSetearArchivo = (file) => {
    setError(null);
    setResultado(null);

    const tiposPermitidos = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    if (!tiposPermitidos.includes(file.type)) {
      setError('Tipo de archivo no permitido. Use CSV o Excel (.xlsx)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('El archivo excede el tamaño máximo de 5MB');
      return;
    }

    setArchivo(file);
  };

  const handleDescargarPlantilla = async () => {
    try {
      const response = await asignacionesAPI.descargarPlantilla();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_asignaciones.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Error al descargar la plantilla');
    }
  };

  const handleImportar = async () => {
    if (!archivo) return;

    setCargando(true);
    setError(null);
    setResultado(null);

    const formData = new FormData();
    formData.append('archivo', archivo);
    formData.append('modo', modo);
    formData.append('sobrescribir', sobrescribir);

    try {
      const response = await asignacionesAPI.importar(formData);
      setResultado(response.data.data);

      if (response.data.data.exitosos > 0 && onImportComplete) {
        onImportComplete();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Error al importar el archivo');
    } finally {
      setCargando(false);
    }
  };

  const limpiar = () => {
    setArchivo(null);
    setResultado(null);
    setError(null);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Importar Asignaciones</h2>
            <p className="text-sm text-gray-600 mt-1">
              Carga masiva de asignaciones desde CSV o Excel
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Descargar plantilla */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="w-6 h-6 text-blue-600" />
              <div>
                <p className="font-medium text-blue-900">Plantilla de ejemplo</p>
                <p className="text-sm text-blue-700">Descarga la plantilla con el formato correcto</p>
              </div>
            </div>
            <button
              onClick={handleDescargarPlantilla}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              Descargar
            </button>
          </div>

          {/* Dropzone */}
          {!resultado && (
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                arrastrando
                  ? 'border-blue-500 bg-blue-50'
                  : archivo
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              {archivo ? (
                <div className="flex items-center justify-center gap-4">
                  <FileSpreadsheet className="w-12 h-12 text-green-600" />
                  <div className="text-left">
                    <p className="font-medium text-gray-900">{archivo.name}</p>
                    <p className="text-sm text-gray-600">
                      {(archivo.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <button
                    onClick={limpiar}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
              ) : (
                <>
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-2">
                    Arrastra y suelta un archivo aquí, o
                  </p>
                  <label className="inline-block cursor-pointer">
                    <input
                      type="file"
                      onChange={handleFileSelect}
                      accept=".csv,.xlsx,.xls"
                      className="hidden"
                    />
                    <span className="text-blue-600 hover:text-blue-700 font-medium">
                      selecciona un archivo
                    </span>
                  </label>
                  <p className="text-sm text-gray-500 mt-2">
                    CSV o Excel (.xlsx) - Máximo 5MB / 1000 filas
                  </p>
                </>
              )}
            </div>
          )}

          {/* Opciones de importación */}
          {archivo && !resultado && (
            <div className="mt-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Modo de importación
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="modo"
                      value="estricto"
                      checked={modo === 'estricto'}
                      onChange={(e) => setModo(e.target.value)}
                      className="text-blue-600"
                    />
                    <span className="text-sm">
                      <span className="font-medium">Estricto</span>
                      <span className="text-gray-500 ml-1">
                        (cancela todo si hay errores)
                      </span>
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="modo"
                      value="permisivo"
                      checked={modo === 'permisivo'}
                      onChange={(e) => setModo(e.target.value)}
                      className="text-blue-600"
                    />
                    <span className="text-sm">
                      <span className="font-medium">Permisivo</span>
                      <span className="text-gray-500 ml-1">
                        (importa filas válidas)
                      </span>
                    </span>
                  </label>
                </div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sobrescribir}
                  onChange={(e) => setSobrescribir(e.target.checked)}
                  className="rounded text-blue-600"
                />
                <span className="text-sm">
                  Sobrescribir asignaciones existentes con fechas solapadas
                </span>
              </label>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mt-4 p-4 bg-red-50 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* Resultado */}
          {resultado && (
            <div className="space-y-4">
              {/* Resumen */}
              <div className={`p-4 rounded-lg ${
                resultado.exitosos > 0 ? 'bg-green-50' : 'bg-yellow-50'
              }`}>
                <div className="flex items-center gap-3 mb-3">
                  <CheckCircle className={`w-6 h-6 ${
                    resultado.exitosos > 0 ? 'text-green-600' : 'text-yellow-600'
                  }`} />
                  <span className="font-medium text-gray-900">
                    Importación completada
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{resultado.total}</p>
                    <p className="text-sm text-gray-600">Total filas</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">{resultado.exitosos}</p>
                    <p className="text-sm text-gray-600">Importadas</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-600">{resultado.fallidos}</p>
                    <p className="text-sm text-gray-600">Con errores</p>
                  </div>
                </div>
                {resultado.mensaje && (
                  <p className="mt-3 text-sm text-gray-600 text-center">{resultado.mensaje}</p>
                )}
              </div>

              {/* Lista de errores */}
              {resultado.errores && resultado.errores.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b">
                    <span className="font-medium text-gray-700">
                      Errores por fila ({resultado.errores.length})
                    </span>
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {resultado.errores.map((errorItem, index) => (
                      <div key={index} className="px-4 py-3 border-b last:border-b-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded">
                            Fila {errorItem.fila}
                          </span>
                        </div>
                        <ul className="text-sm text-gray-600 space-y-1">
                          {errorItem.errores.map((err, errIndex) => (
                            <li key={errIndex}>
                              {err.campo !== 'general' && (
                                <span className="font-medium">{err.campo}: </span>
                              )}
                              {err.mensaje}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Botón para importar otro archivo */}
              <button
                onClick={limpiar}
                className="w-full py-2 text-blue-600 hover:text-blue-700 font-medium"
              >
                Importar otro archivo
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50 flex gap-4">
          <button
            onClick={onClose}
            className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {resultado ? 'Cerrar' : 'Cancelar'}
          </button>
          {!resultado && (
            <button
              onClick={handleImportar}
              disabled={!archivo || cargando}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {cargando ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Importando...
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  Importar
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ImportadorAsignaciones;
